import React, { useRef, useEffect, useLayoutEffect, useMemo, useState } from 'react';
import type { CellContext, ColumnDef } from '@tanstack/react-table';
import { clsx } from 'clsx';
import { getColumnId } from '@/types/table.types';
import { TradeItem, TableSection, type MoneySummary, type RowSelectionMode } from '@/types';
import {
  SortableContext,
  useSortable,
} from '@dnd-kit/sortable';
import type { SortingStrategy } from '@dnd-kit/sortable';
import { ColumnHeader } from '@/features/inventory/components/helpers/ColumnHeader';
import {
  buildDisplayRows,
  isRequireAttention,
  staleMaturedDays,
  staleRecallDays,
  stuckBoPosting,
  stuckBoPostingTitle,
} from '@/utils/trades/section-grouping';
import { useInfiniteScrollLoader } from '@/hooks/common';
import { selectionModeFromEvent } from '@/features/trades/hooks/use-row-selection';

/** In-row controls that own their own pointer and key events — never row selection. */
const ROW_SELECT_EXEMPT =
  'button, a, input, select, textarea, [role="menuitem"], [data-no-row-select]';
import { useSettingsQueries } from '@/hooks/settings/use-settings-queries';
import { todayUtcKey } from '@/utils/format';
import { isServerOrderable } from '@/utils/trades/map-server-params';
import type { DisplayRow } from '@/types';
import { storeTradesColumnWidths } from './trades-width-storage';
import type { ColumnPinSide } from '@/stores/trades/trades-column-store';
import { highlightText } from '@/utils/inventory/search-highlight';
import { Z_INDEX } from '@/utils/z-index';
import { buildFooterTotalsFromServer, approxRateNote } from '@/utils/trades/footer-totals';
import { SUMMABLE_COLUMNS, CLIENT_ONLY_FOOTER_COLUMN_IDS } from '@/utils/trades/csv-totals';
import { useCurrencyDisplayStore, useFxRatesStore } from '@/stores';

import { useTradesCrossDnd } from '../TradesCrossDndContext';
import { FAB_CLEARANCE_PX } from '../fab-geometry';
import { GripDotsIcon, smoothScroll as sharedSmoothScroll, getColWidth as sharedGetColWidth } from '@/components/shared/table-utils';
import { VirtualizedGrid } from '@/components/shared/VirtualizedGrid';
import {
  GRID_HEADER_HEIGHT_PX,
  GRID_HEADER_HEIGHT_STYLE,
  GRID_ROW_HEIGHT_PX,
  GRID_ROW_HEIGHT_STYLE,
} from '@/components/shared/grid-style';

const noDisplacementStrategy: SortingStrategy = () => null;

type TradeColumnDef = ColumnDef<TradeItem, unknown>;

const COL_GAP_PX = 12;

// Width (px) of the sticky "Total" label that pins to the footer's left edge.
// A negative margin of the same value cancels its layout width so it OVERLAYS
// the leftmost (always non-summable, blank) footer cells rather than shifting
// every column right and breaking alignment with the header/body grid.
const TOTAL_LABEL_WIDTH_PX = 72;

type SortableHeaderWrapperProps = {
  id: string;
  children: (handle: React.ReactNode) => React.ReactNode;
  enabled: boolean;
  showHandle?: boolean;
  className?: string;
  style?: React.CSSProperties;
  onContextMenu?: (e: React.MouseEvent) => void;
};

const SortableHeaderWrapper: React.FC<SortableHeaderWrapperProps> = ({
  id,
  children,
  enabled,
  showHandle = true,
  className,
  style,
  onContextMenu,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    isDragging,
  } = useSortable({ id, disabled: !enabled });
  const { activeDragSource, tableOverColumnId } = useTradesCrossDnd();
  const showDropIndicator = tableOverColumnId === id && activeDragSource != null;

  const dragStyle: React.CSSProperties = {
    ...style,
    visibility: (isDragging && activeDragSource === 'table') ? 'hidden' as const : undefined,
  };

  const handle = enabled && showHandle ? (
    <span
      className="flex flex-shrink-0 items-center justify-center cursor-grab active:cursor-grabbing text-gray-400 hover:text-gray-600 self-center"
      {...attributes}
      {...listeners}
      tabIndex={0}
      role="button"
      aria-label="Drag to reorder column"
    >
      <GripDotsIcon className="h-4 w-4" />
    </span>
  ) : null;

  return (
    <div
      ref={setNodeRef}
      className={className}
      style={dragStyle}
      onContextMenu={onContextMenu}
      data-column-id={id}
    >
      {showDropIndicator && (
        <div className="absolute left-0 top-0 bottom-0 w-0.5 bg-blue-500 rounded-full z-10 pointer-events-none" />
      )}
      {children(handle)}
    </div>
  );
};

const SKELETON_ROW_COUNT_DEFAULT = 30;

/** Placeholder for a cell whose value is still loading. */
const CellSkeleton: React.FC = () => (
  <div aria-hidden="true" className="h-3.5 w-[70%] rounded bg-gray-200 motion-safe:animate-pulse" />
);

const PIN_ICON_EXTRA_PX = 22;
const FILTER_ICON_EXTRA_PX = 24;

const getColWidth = (
  col: TradeColumnDef,
  columnWidths: Record<string, number>,
  pinnedSet?: Set<string>,
  filterableSet?: Set<string>,
): number => {
  const cid = getColumnId(col);
  return sharedGetColWidth(cid, columnWidths[cid], col.minSize, pinnedSet, filterableSet, PIN_ICON_EXTRA_PX, FILTER_ICON_EXTRA_PX);
};

type TradesTableProps = {
  sections: TableSection[];
  columns: TradeColumnDef[];
  sorting: { id: string; desc: boolean }[];
  onSortChange: (sorting: { id: string; desc: boolean }[]) => void;
  columnWidths: Record<string, number>;
  /** No rows to show yet (first load): the header stays, the body is skeleton rows. */
  isLoading: boolean;
  /** A new selection (sort, search, tab, filter) is loading while the previous rows
   *  stay on screen as placeholder data. */
  isRefreshing?: boolean;
  error: Error | null;
  hoveredColumn: string | null;
  onHoveredColumnChange: (column: string | null) => void;
  onShowScrollTop: (show: boolean) => void;
  scrollContainerRef?: React.RefObject<HTMLDivElement>;
  // Selection props (views system)
  selectedIds: Set<number>;
  hasSelection: boolean;
  /** LR-600: scroll clearance below the last row, sized to the floating bar's
   *  measured height (it wraps to two button rows on narrow viewports). Defaults
   *  to the unwrapped FAB_CLEARANCE_PX until the bar reports a height. */
  fabClearancePx?: number;
  /** Whole-row click (or Space/Enter on it) → useRowSelection, with the mode the
   *  modifier keys asked for: plain replaces, Cmd/Ctrl toggles, Shift ranges. */
  onRowClick: (id: number, mode: RowSelectionMode) => void;
  // Pinning / DnD / context menu props
  pinnedLeft?: string[];
  pinnedRight?: string[];
  onPinColumn?: (columnId: string, side: ColumnPinSide) => void;
  activeFilterColumnIds?: string[];
  onFilterColumnClick?: (columnId: string) => void;
  filterableColumnIds?: Set<string>;
  onContextMenuColumn?: (e: React.MouseEvent, columnId: string) => void;
  isColumnsTabActive?: boolean;
  highlightedColumnIds?: string[];
  searchQuery?: string;
  /** Empty-state copy shown when there are no rows (e.g. a counterparty tab with no trades). */
  emptyState?: { title: string; description?: string };
  // LR-546: infinite-scroll paging — fetch the next window near the bottom.
  // Promise-returning: the loader releases its lock on the fetch's settle (see
  // useInfiniteScrollLoader), so a void-returning form would silently regress it.
  onLoadMore?: () => Promise<unknown>;
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
  /** Query identity — changing it resets the per-query auto-fill budget. */
  loaderResetKey?: unknown;
  /** LR-546: server-computed WHOLE-BOOK, OPEN-book per-column subtotals for the
   *  footer (GET /positions/metrics/ column_totals). undefined while the summary is
   *  pending — the footer row still renders, cells fill in when it arrives. */
  footerColumnTotals?: Record<string, MoneySummary>;
};

export const TradesTable: React.FC<TradesTableProps> = React.memo(({
  sections,
  emptyState,
  columns,
  sorting,
  onSortChange,
  columnWidths,
  isLoading,
  isRefreshing = false,
  error,
  hoveredColumn,
  onHoveredColumnChange,
  onShowScrollTop,
  scrollContainerRef,
  selectedIds,
  hasSelection,
  fabClearancePx = FAB_CLEARANCE_PX,
  onRowClick,
  pinnedLeft = [],
  pinnedRight = [],
  onPinColumn,
  activeFilterColumnIds,
  onFilterColumnClick,
  filterableColumnIds,
  onContextMenuColumn,
  isColumnsTabActive = false,
  highlightedColumnIds,
  searchQuery = '',
  onLoadMore,
  hasNextPage = false,
  isFetchingNextPage = false,
  loaderResetKey,
  footerColumnTotals,
}) => {
  const internalParentRef = useRef<HTMLDivElement>(null);
  const parentRef = scrollContainerRef || internalParentRef;

  const allItems = useMemo(() => sections.flatMap((s) => s.items), [sections]);
  // Skeleton rows under the header: a first load, or a new selection loading over a
  // previous one that had no rows (its empty state would read as the new answer).
  const showSkeletonBody = isLoading || (isRefreshing && allItems.length === 0);
  const displayRows = useMemo(() => buildDisplayRows(sections), [sections]);

  const toggleSort = (columnId: string) => {
    if (columnId === 'rowNumber') return;
    const existing = sorting.find((s) => s.id === columnId);
    if (!existing) {
      onSortChange([{ id: columnId, desc: false }]);
    } else if (!existing.desc) {
      onSortChange([{ id: columnId, desc: true }]);
    } else {
      onSortChange([]);
    }
  };

  useInfiniteScrollLoader({
    scrollRef: parentRef,
    hasNextPage,
    isFetchingNextPage,
    error: error ?? undefined,
    onLoadMore,
    rowCount: displayRows.length,
    resetKey: loaderResetKey,
  });

  // "Scroll to top" affordance is independent of paging. The sync call on attach
  // re-reads the offset when the skeleton body swaps in or out.
  useEffect(() => {
    const scrollElement = parentRef.current;
    if (!scrollElement) return;
    const handleScroll = () => onShowScrollTop(scrollElement.scrollTop > 500);
    handleScroll();
    scrollElement.addEventListener('scroll', handleScroll);
    return () => scrollElement.removeEventListener('scroll', handleScroll);
  }, [onShowScrollTop, parentRef, showSkeletonBody]);

  // A new selection opens at its first row. The grid stays mounted while it loads
  // (the previous rows hold the screen), so nothing else would reset the offset. Reset
  // when the key changes and again when its rows land: an offset scrolled through the
  // held rows would sit past a shorter first page and pull in the next one. A layout
  // effect, so the reset comes before the clamped offset's scroll event reaches the loader.
  const lastResetKeyRef = useRef(loaderResetKey);
  const wasRefreshingRef = useRef(isRefreshing);
  useLayoutEffect(() => {
    const keyChanged = !Object.is(lastResetKeyRef.current, loaderResetKey);
    const landed = wasRefreshingRef.current && !isRefreshing;
    lastResetKeyRef.current = loaderResetKey;
    wasRefreshingRef.current = isRefreshing;
    if (!keyChanged && !landed) return;
    const el = parentRef.current;
    if (!el) return;
    el.scrollTop = 0;
    onShowScrollTop(false);
  }, [loaderResetKey, isRefreshing, parentRef, onShowScrollTop]);

  // Enough skeleton rows to fill the visible body.
  const [skeletonRowCount, setSkeletonRowCount] = useState(SKELETON_ROW_COUNT_DEFAULT);
  useLayoutEffect(() => {
    if (!showSkeletonBody) return;
    const el = parentRef.current;
    if (!el) return;
    setSkeletonRowCount(Math.max(1, Math.ceil((el.clientHeight - GRID_HEADER_HEIGHT_PX) / GRID_ROW_HEIGHT_PX)));
  }, [showSkeletonBody, parentRef]);

  // Persist column widths so the next load starts from them (calculateTradesColumnWidths)
  useEffect(() => {
    if (!isLoading && allItems.length > 0) {
      storeTradesColumnWidths(columnWidths);
    }
  }, [isLoading, allItems.length, columnWidths]);

  // Ctrl+Arrow: scroll to edges
  useEffect(() => {
    const animIdRef = { current: 0 };
    const handler = (e: KeyboardEvent) => {
      if (!e.ctrlKey && !e.metaKey) return;
      const el = parentRef.current;
      if (!el) return;
      const target = e.target as HTMLElement;
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable) return;
      switch (e.key) {
        case 'ArrowUp':
          e.preventDefault();
          sharedSmoothScroll(el, 0, el.scrollLeft, animIdRef);
          break;
        case 'ArrowDown':
          e.preventDefault();
          sharedSmoothScroll(el, el.scrollHeight, el.scrollLeft, animIdRef);
          break;
        case 'ArrowLeft':
          e.preventDefault();
          sharedSmoothScroll(el, el.scrollTop, 0, animIdRef);
          break;
        case 'ArrowRight':
          e.preventDefault();
          sharedSmoothScroll(el, el.scrollTop, el.scrollWidth, animIdRef);
          break;
      }
    };
    document.addEventListener('keydown', handler);
    return () => {
      document.removeEventListener('keydown', handler);
      cancelAnimationFrame(animIdRef.current);
    };
  }, [parentRef]);

  const pinnedLeftSet = useMemo(() => new Set(pinnedLeft), [pinnedLeft]);
  const pinnedRightSet = useMemo(() => new Set(pinnedRight), [pinnedRight]);
  const allPinnedSet = useMemo(() => new Set([...pinnedLeft, ...pinnedRight]), [pinnedLeft, pinnedRight]);

  const { leftCols, centerCols, rightCols, leftWidth, rightWidth } = useMemo(() => {
    const left: TradeColumnDef[] = [];
    const center: TradeColumnDef[] = [];
    const right: TradeColumnDef[] = [];
    columns.forEach((col) => {
      const cid = getColumnId(col);
      if (pinnedLeftSet.has(cid)) left.push(col);
      else if (pinnedRightSet.has(cid)) right.push(col);
      else center.push(col);
    });
    let lw = 0;
    for (const col of left) lw += getColWidth(col, columnWidths, allPinnedSet, filterableColumnIds) + COL_GAP_PX;
    let rw = 0;
    for (const col of right) rw += getColWidth(col, columnWidths, allPinnedSet, filterableColumnIds) + COL_GAP_PX;
    return { leftCols: left, centerCols: center, rightCols: right, leftWidth: lw + (left.length > 0 ? 10 : 0), rightWidth: rw + (right.length > 0 ? 16 : 0) };
  }, [columns, columnWidths, pinnedLeftSet, pinnedRightSet, allPinnedSet, filterableColumnIds]);

  const hasPinnedLeft = leftCols.length > 0;
  const hasPinnedRight = rightCols.length > 0;

  const headerColumnIds = useMemo(
    () => columns.map((c) => getColumnId(c)),
    [columns]
  );

  // ── LR-469 bottom column-totals footer (LR-546: WHOLE-BOOK, server-fed) ──
  // This footer used to total the loaded page client-side; once LR-546 windowed the
  // blotter that silently became "over the visible window" (and vanished entirely
  // when the first page held no open-book rows). It now reads the server's OPEN-book
  // per-column subtotals (`/positions/metrics/` column_totals → footerColumnTotals),
  // so it's whole-book and consistent with the metrics bar above it. base drives only
  // the multi-currency `≈` roll-up; native single-currency totals never convert.
  // Subscribing to the store re-renders when the trader flips the EUR/USD base.
  const baseCurrency = useCurrencyDisplayStore((s) => s.baseCurrency);
  // Reactive FX signal: recompute the ≈ roll-up when live rates land after a cold-load
  // race, instead of staying frozen on the fallback figure + ⚠ (LR-469 hooks-1).
  const fxVersion = useFxRatesStore((s) => s.version);
  // LR-673: read once per render, not per row — the settings query is cached
  // (10min staleTime) so this call is free once anything on the page has
  // already fetched it. today is a plain string computation, cheap to redo
  // per render; todayUtcKey (NOT todayLocalKey) so the FE gate agrees with the
  // backend's own timezone.now().date() (see its doc comment).
  const tradesSettings = useSettingsQueries().data;
  const maturedStaleDaysThreshold = tradesSettings?.matured_stale_days_threshold;
  // LR-703: sibling knob — see RECALL_STALE_DAYS_THRESHOLD (settings/base.py).
  const recallStaleDaysThreshold = tradesSettings?.recall_stale_days_threshold;
  const today = todayUtcKey();
  const footerTotals = useMemo(
    () => buildFooterTotalsFromServer(footerColumnTotals, headerColumnIds, baseCurrency),
    // fxVersion is an intentional extra dep (unused in the body): the ≈ roll-up reads live
    // FX through currency-service module state, so this forces a recompute when rates land.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [footerColumnTotals, headerColumnIds, baseCurrency, fxVersion],
  );
  // The footer's PRESENCE tracks the SERVER aggregate + whether a summable column is
  // visible — NEVER the loaded window (the LR-546 regression). So it never vanishes on
  // a filled grid, yet stays absent where there's no server summary (History mode, or
  // the initial load before the summary lands). Server columns render their whole-book
  // total (base-zero when the book has none); rate-engine / COB columns render "—".
  const hasFooterTotals = useMemo(
    () => footerColumnTotals != null && headerColumnIds.some((id) => id in SUMMABLE_COLUMNS),
    [footerColumnTotals, headerColumnIds],
  );

  const renderCell = (column: TradeColumnDef, item: TradeItem, virtualItemIndex: number) => {
    const columnId = getColumnId(column);

    if (!columnId) {
      return column.cell && typeof column.cell === 'function'
        ? column.cell({
            row: { original: item, index: virtualItemIndex },
          } as unknown as CellContext<TradeItem, unknown>)
        : '';
    }

    // A column with an accessor function shows what it derives, not the raw field.
    const cellValue = 'accessorFn' in column
      ? column.accessorFn(item, virtualItemIndex)
      : item[columnId as keyof TradeItem];
    let cellContent = column.cell && typeof column.cell === 'function'
      ? column.cell({
          getValue: () => cellValue,
          row: { original: item, index: virtualItemIndex },
        } as unknown as CellContext<TradeItem, unknown>)
      : String(cellValue || '');

    // Apply search highlighting
    if (searchQuery.trim() && cellValue !== null && cellValue !== undefined && columnId !== 'rowNumber') {
      const stringValue = String(cellValue);
      const query = searchQuery.trim().toLowerCase();
      if (stringValue.toLowerCase().includes(query)) {
        const highlighted = highlightText(stringValue, searchQuery);
        if (React.isValidElement(cellContent)) {
          cellContent = React.cloneElement(cellContent as React.ReactElement, { children: highlighted });
        } else {
          cellContent = highlighted;
        }
      }
    }

    return cellContent;
  };

  const { activeDragSource, sidebarOverColumnId } = useTradesCrossDnd();
  const isSidebarDragOverTable =
    activeDragSource === 'sidebar' &&
    sidebarOverColumnId != null &&
    !sidebarOverColumnId.startsWith('sidebar:');
  const tableOverColumnId = sidebarOverColumnId && !sidebarOverColumnId.startsWith('sidebar:') ? sidebarOverColumnId : null;

  const renderHeaderGroup = (cols: TradeColumnDef[]) =>
    cols.map((column) => {
      const columnId = getColumnId(column);
      const sortState = sorting.find((s) => s.id === columnId);
      const width = getColWidth(column, columnWidths, allPinnedSet, filterableColumnIds);
      const headerText = typeof column.header === 'string' ? column.header : 'Column';
      const align = column.meta?.align || 'left';
      const headerAlign = align === 'center' ? 'left' : align;
      const pinSide: ColumnPinSide = columnId === 'rowNumber' ? null : pinnedLeftSet.has(columnId) ? 'left' : pinnedRightSet.has(columnId) ? 'right' : null;
      const isDraggable = columnId !== 'rowNumber';
      const isHighlighted = highlightedColumnIds?.includes(columnId) ?? false;

      return (
        <SortableHeaderWrapper
          key={columnId}
          id={columnId}
          enabled={isDraggable}
          showHandle={isColumnsTabActive}
          className={clsx('relative px-1.5 box-content flex flex-shrink-0 items-center', isHighlighted && 'self-stretch -my-2 py-2 bg-blue-50 animate-column-highlight-out')}
          style={{
            width: `${width}px`,
            minWidth: `${width}px`,
          }}
          onContextMenu={onContextMenuColumn ? (e) => onContextMenuColumn(e, columnId) : undefined}
        >
          {(handle) => (
            <>
              {isSidebarDragOverTable && tableOverColumnId === columnId && (
                <div className="absolute left-0 top-0 bottom-0 w-0.5 bg-blue-500 rounded-full z-10" />
              )}
              <ColumnHeader
                columnId={columnId}
                headerText={headerText}
                width={width}
                sortState={sortState}
                sortable={isServerOrderable(columnId)}
                onSort={toggleSort}
                isLoading={isLoading}
                hasError={!!error}
                hoveredColumn={hoveredColumn}
                onHover={onHoveredColumnChange}
                align={headerAlign}
                pinSide={pinSide}
                onUnpin={onPinColumn ? () => onPinColumn(columnId, null) : undefined}
                filterable={filterableColumnIds?.has(columnId)}
                onFilterClick={onFilterColumnClick}
                hasActiveFilter={activeFilterColumnIds?.includes(columnId)}
                hideControls={isColumnsTabActive}
                dragHandle={handle}
              />
            </>
          )}
        </SortableHeaderWrapper>
      );
    });

  // `item` null renders a skeleton row with the same cell geometry as a real one.
  const renderBodyGroup = (cols: TradeColumnDef[], item: TradeItem | null, rowIndex: number) =>
    cols.map((column) => {
      const columnId = getColumnId(column);
      const width = getColWidth(column, columnWidths, allPinnedSet, filterableColumnIds);
      const align = column.meta?.align || 'left';
      const isCellHighlighted = highlightedColumnIds?.includes(columnId) ?? false;
      const isRowNumberCol = columnId === 'rowNumber';
      const isCellInPinZone = pinnedLeftSet.has(columnId) || pinnedRightSet.has(columnId) || isRowNumberCol;

      return (
        <div
          key={columnId}
          className={clsx(
            'px-1.5 box-content text-sm self-stretch flex items-center min-w-0',
            !isCellInPinZone && '-my-2 py-2',
            align === 'right' ? 'justify-end' : align === 'center' ? 'justify-center' : 'justify-start',
            columnId !== 'rowNumber' && columnId !== 'status' && columnId !== 'collateral_type'
              && 'overflow-hidden text-ellipsis whitespace-nowrap',
            isCellHighlighted && 'bg-blue-50 animate-column-highlight-out',
          )}
          style={{
            width: `${width}px`,
            minWidth: `${width}px`,
          }}
        >
          {item ? renderCell(column, item, rowIndex) : <CellSkeleton />}
        </div>
      );
    });

  // LR-469: footer cell per column — same width + per-cell padding (px-1.5 box-content)
  // as the header/body groups, so totals line up under their columns. Summable columns
  // show their total (right-aligned, mono); the rest render blank (the "Total" row
  // label is a separate left-pinned overlay, see footerNode).
  const renderFooterGroup = (cols: TradeColumnDef[]) =>
    cols.map((column) => {
      const columnId = getColumnId(column);
      const width = getColWidth(column, columnWidths, allPinnedSet, filterableColumnIds);
      const total = footerTotals[columnId];
      // Rate-engine / COB columns aren't summed whole-book server-side (LR-546) — show
      // an honest em dash rather than a loaded-window figure that would contradict the
      // whole-book bar. A server column the server DID total but found nothing for stays
      // blank (not a base-zero over a column of "—" cells) — see buildFooterTotalsFromServer.
      const clientOnly = !total && CLIENT_ONLY_FOOTER_COLUMN_IDS.includes(columnId);
      const align = column.meta?.align || 'left';
      return (
        <div
          key={columnId}
          className={clsx(
            'px-1.5 box-content flex items-center self-stretch min-w-0 whitespace-nowrap',
            align === 'right' ? 'justify-end' : align === 'center' ? 'justify-center' : 'justify-start',
          )}
          style={{ width: `${width}px`, minWidth: `${width}px` }}
        >
          {total ? (
            <span className="truncate font-mono tabular-nums text-gray-900" title={total.title || undefined}>
              {total.text}
              {total.approx && (
                <span
                  className="ml-0.5 text-amber-600"
                  role="img"
                  aria-label={approxRateNote(total.approxCurrencies)}
                  title={approxRateNote(total.approxCurrencies)}
                >
                  ⚠
                </span>
              )}
            </span>
          ) : clientOnly ? (
            <span
              className="truncate font-mono tabular-nums text-gray-400"
              title="Not totalled over the full book — computed per loaded row only"
            >
              —
            </span>
          ) : null}
        </div>
      );
    });

  const renderSectionHeader = (row: Extract<DisplayRow, { type: 'section-header' }>) => (
    <div
      data-section-id={row.sectionId}
      className="flex items-center bg-gray-100 px-4 text-xs font-semibold uppercase tracking-wider text-gray-500"
      style={{ height: '32px' }}
    >
      {row.title} ({row.count})
    </div>
  );

  const renderSkeletonRow = (index: number) => (
    <div
      key={index}
      data-skeleton-row=""
      className="relative flex items-center border-b border-gray-200"
      style={GRID_ROW_HEIGHT_STYLE}
    >
      {hasPinnedLeft && (
        <div
          className="sticky left-0 flex items-center self-stretch bg-white pl-3 pr-3 py-2 overflow-hidden shadow-[4px_0_6px_-2px_rgba(0,0,0,0.1)]"
          style={{ minWidth: `${leftWidth}px`, width: `${leftWidth}px`, zIndex: Z_INDEX.TABLE_STICKY }}
        >
          {renderBodyGroup(leftCols, null, index)}
        </div>
      )}
      <div className={clsx('flex items-center py-2', hasPinnedLeft ? 'pl-2' : 'pl-4', hasPinnedRight ? 'pr-2' : 'pr-4')}>
        {renderBodyGroup(centerCols, null, index)}
      </div>
      {hasPinnedRight && (
        <div
          className="sticky right-0 flex items-center self-stretch bg-white pl-3 pr-7 py-2 shadow-[-4px_0_6px_-2px_rgba(0,0,0,0.1)]"
          style={{ minWidth: `${rightWidth}px`, width: `${rightWidth}px`, zIndex: Z_INDEX.TABLE_STICKY }}
        >
          {renderBodyGroup(rightCols, null, index)}
        </div>
      )}
    </div>
  );

  const headerNode = (
    <SortableContext items={headerColumnIds} strategy={noDisplacementStrategy}>
      {hasPinnedLeft && (
        <div
          className="sticky left-0 flex items-center self-stretch bg-gray-50 pl-3 pr-3 shadow-[4px_0_6px_-2px_rgba(0,0,0,0.1)]"
          style={{ minWidth: `${leftWidth}px`, width: `${leftWidth}px`, zIndex: Z_INDEX.TABLE_STICKY_HEADER }}
        >
          {renderHeaderGroup(leftCols)}
        </div>
      )}
      <div className={clsx('flex items-center self-stretch py-2', hasPinnedLeft ? 'pl-2' : 'pl-4', hasPinnedRight ? 'pr-2' : 'pr-4')}>
        {renderHeaderGroup(centerCols)}
      </div>
      {hasPinnedRight && (
        <div
          className="sticky right-0 flex items-center self-stretch bg-gray-50 pl-3 pr-7 shadow-[-4px_0_6px_-2px_rgba(0,0,0,0.1)]"
          style={{ minWidth: `${rightWidth}px`, width: `${rightWidth}px`, zIndex: Z_INDEX.TABLE_STICKY_HEADER }}
        >
          {renderHeaderGroup(rightCols)}
        </div>
      )}
    </SortableContext>
  );

  // LR-469: sticky bottom totals row. Mirrors headerNode's left/center/right pinned
  // zones (same widths, paddings, sticky shadows) so every column total sits under its
  // column on both axes of scroll. Rendered only when at least one summable column is
  // visible; otherwise undefined so VirtualizedGrid draws no footer (and the other grids
  // that never pass `footer` stay untouched).
  const footerNode = hasFooterTotals ? (
    <div
      className="flex items-stretch border-t border-gray-200 bg-gray-50 text-sm font-medium text-gray-900"
      style={GRID_HEADER_HEIGHT_STYLE}
      data-trades-table-footer
    >
      {/* "Total" row label — pinned to the left edge so it never scrolls out of
          view horizontally (its old "Σ" sat in the first column cell and slid off
          on scroll). The negative margin cancels its own width so it overlays the
          leftmost blank cells instead of shifting every column and breaking
          alignment. z-20 keeps it above the pinned-zone cells (z-12). */}
      <div
        data-trades-total-label
        className="sticky left-0 z-20 flex items-center self-stretch bg-gray-50 pl-4 pr-3 text-xs font-semibold uppercase tracking-wide text-gray-500"
        style={{ width: `${TOTAL_LABEL_WIDTH_PX}px`, marginRight: `-${TOTAL_LABEL_WIDTH_PX}px` }}
        title={`Column totals over the whole open book (excludes pending / rejected positions) — multi-currency columns shown as their ≈ ${baseCurrency} equivalent. Rate-engine / COB columns (shown "—") are not totalled book-wide.`}
      >
        Total
      </div>
      {hasPinnedLeft && (
        <div
          className="sticky left-0 flex items-center self-stretch bg-gray-50 pl-3 pr-3 shadow-[4px_0_6px_-2px_rgba(0,0,0,0.1)]"
          style={{ minWidth: `${leftWidth}px`, width: `${leftWidth}px`, zIndex: Z_INDEX.TABLE_STICKY_HEADER }}
        >
          {renderFooterGroup(leftCols)}
        </div>
      )}
      <div className={clsx('flex items-center self-stretch', hasPinnedLeft ? 'pl-2' : 'pl-4', hasPinnedRight ? 'pr-2' : 'pr-4')}>
        {renderFooterGroup(centerCols)}
      </div>
      {hasPinnedRight && (
        <div
          className="sticky right-0 flex items-center self-stretch bg-gray-50 pl-3 pr-7 shadow-[-4px_0_6px_-2px_rgba(0,0,0,0.1)]"
          style={{ minWidth: `${rightWidth}px`, width: `${rightWidth}px`, zIndex: Z_INDEX.TABLE_STICKY_HEADER }}
        >
          {renderFooterGroup(rightCols)}
        </div>
      )}
    </div>
  ) : undefined;

  // The next page is requested a couple of rows before the end, so a row appended under
  // the body would sit below the fold. The indicator overlays the visible bottom edge
  // instead (above the totals, out of the layout). A sticky inset is a share of the
  // scrollport, so `left-1/2` keeps it centred in view under horizontal scroll.
  // A new selection loading over the previous rows uses the same indicator.
  const loadingLabel = isRefreshing ? 'Loading trades…' : isFetchingNextPage ? 'Loading more trades…' : null;
  const loadingNode = loadingLabel ? (
    <div
      className="pointer-events-none absolute inset-x-0 bottom-full"
      // Above the FloatingActionBar, which shares the bottom centre while rows are selected.
      // The clearance nets out the totals strip, so without one its height goes back in.
      style={hasSelection
        ? { paddingBottom: `${fabClearancePx + (footerNode ? 0 : GRID_HEADER_HEIGHT_PX)}px` }
        : undefined}
    >
      <div
        aria-hidden="true"
        data-trades-loading-indicator=""
        className="sticky left-1/2 my-2 flex w-fit -translate-x-1/2 items-center gap-2 rounded-full border border-gray-200 bg-white px-4 py-1.5 text-sm text-gray-700 shadow-md"
      >
        <span
          aria-hidden="true"
          className="h-4 w-4 rounded-full border-2 border-gray-300 border-t-blue-600 motion-safe:animate-spin"
        />
        {loadingLabel}
      </div>
    </div>
  ) : null;

  let bodyOverride: React.ReactNode = null;
  if (showSkeletonBody) {
    bodyOverride = (
      <div data-trades-table-skeleton="">
        {Array.from({ length: skeletonRowCount }, (_, i) => renderSkeletonRow(i))}
      </div>
    );
  } else if (error) {
    bodyOverride = (
      <div className="flex items-center ml-[37vw] py-12">
        <div className="text-center">
          <div className="mb-2 text-red-600">
            <svg
              className="mx-auto h-12 w-12"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
              />
            </svg>
          </div>
          <h3 className="text-base font-medium text-red-600">Failed to load trades</h3>
          <p className="mt-1 text-sm text-gray-500">Refresh the page or contact support if the problem persists.</p>
        </div>
      </div>
    );
  } else if (allItems.length === 0) {
    // Centered into the visible viewport (the body scrolls horizontally
    // across the full table width, so plain justify-center lands off-screen).
    bodyOverride = (
      <div className="flex items-center ml-[37vw] py-12">
        <div className="text-center">
          <h3 className="text-base font-medium text-gray-700">
            {emptyState?.title ?? 'No trades found'}
          </h3>
          <p className="mt-1 text-sm text-gray-500">
            {emptyState?.description ?? 'Try adjusting your filters or search query.'}
          </p>
        </div>
      </div>
    );
  }

  const grid = (
    <VirtualizedGrid<DisplayRow>
      rows={displayRows}
      estimateRowHeight={(index) =>
        displayRows[index]?.type === 'section-header' ? 32 : GRID_ROW_HEIGHT_PX
      }
      overscan={10}
      scrollContainerRef={parentRef}
      scrollContainerProps={
        {
          'data-trades-table': true,
          'data-trades-table-scroll-container': true,
          'aria-busy': isLoading || isRefreshing || undefined,
        } as React.HTMLAttributes<HTMLDivElement>
      }
      header={headerNode}
      headerClassName="shrink-0"
      headerStyle={GRID_HEADER_HEIGHT_STYLE}
      bodyOverride={bodyOverride}
      // virt-1 (revised): the column-totals footer ALWAYS pins to bottom-0 — the
      // FloatingActionBar floats ABOVE it (FAB_BOTTOM_OFFSET_PX clears the strip),
      // so the totals stay put and no body rows peek beneath the full-width strip
      // (the old lift-the-footer fix did exactly that). Only the body keeps a
      // selection-time paddingBottom so the last data row can scroll clear of the
      // FAB; the constant lives next to the FAB's geometry (kvi #5).
      //
      // box-sizing: the virtualized body div carries an explicit `height` (the
      // virtualizer's total size) under the global `border-box`, so a plain
      // paddingBottom would be absorbed INTO that height and add no scroll room —
      // the last rows would stay pinned behind the FAB. content-box makes the pad
      // extend the box, giving the genuine extra scroll the clearance needs.
      bodyStyle={hasSelection ? { paddingBottom: `${fabClearancePx}px`, boxSizing: 'content-box' } : undefined}
      footer={loadingNode || footerNode ? <>{loadingNode}{footerNode}</> : undefined}
      renderRow={({ row: displayRow, index, virtualRow, style }) => (
        <div
          key={virtualRow?.key ?? index}
          style={style}
          // The held rows of a loading selection take no click, focus or key: a click
          // would focus a row it does not select. A spread: React 18 has no `inert` prop.
          {...(isRefreshing ? { inert: '' } : {})}
        >
          {displayRow.type === 'section-header'
            ? renderSectionHeader(displayRow)
            : (() => {
                const item = displayRow.item;
                const isRowSelected = selectedIds.has(item.id);
                // LR-673: Matured with no Close Settlement Date, stale past the
                // threshold — the conditional half of "requires attention"
                // (isRequireAttention covers the unconditional pending/rejected
                // statuses). null while settings hasn't loaded yet, so the
                // signal only lights up once the real threshold is known.
                const staleDays =
                  maturedStaleDaysThreshold != null
                    ? staleMaturedDays(item, maturedStaleDaysThreshold, today)
                    : null;
                // LR-686: attention rows (pending / rejected, or LR-673 stale-matured)
                // are no longer bucketed above the book — an amber edge marks them in
                // place. It lives inside the pinned-left group when there is one, so
                // it stays put under horizontal scroll; under selection the blue edge
                // wins.
                // LR-703: a pending recall/return past its contractual settlement
                // date — the same class of quiet stall, and quieter still: the row
                // reads `Pending Partial Settlement` over a stored `active`, so no
                // ATTENTION_STATUS covers it.
                const staleRecall =
                  recallStaleDaysThreshold != null
                    ? staleRecallDays(item, recallStaleDaysThreshold, today)
                    : null;
                // LR-711: a BackOffice posting that never resolved. The quietest
                // stall of the three — no status covers it, no date is overdue, and
                // until now it was visible only in Django-admin (TRD-111 sat 25 days
                // that way). Unconditional, unlike its two siblings: there is no
                // threshold to wait out, because the backend only sends this for a
                // posting that has already failed or is already lost.
                const stuckPosting = stuckBoPosting(item);
                const needsAttention =
                  !isRowSelected &&
                  (isRequireAttention(item) ||
                    staleDays != null ||
                    staleRecall != null ||
                    stuckPosting != null);
                const attentionAccent = needsAttention ? (
                  <span
                    aria-hidden="true"
                    data-attention-accent=""
                    className="absolute left-0 top-0 bottom-0 w-0.5 bg-amber-400 pointer-events-none"
                  />
                ) : null;
                // LR-673: the accent bar is 2px and pointer-events-none (clicks must
                // pass through to the row), so the tooltip lives on the row itself —
                // also a far more hoverable target than the bar.
                // Both conditional signals share the row title. They can co-occur
                // (a matured trade can carry an overdue recall), so the tooltip names
                // each one that applies rather than letting one mask the other.
                const staleTitles = [
                  staleDays != null
                    ? `Matured ${staleDays} day${staleDays === 1 ? '' : 's'} ago, no close date — set a close date or terminate`
                    : null,
                  staleRecall != null
                    ? `Recall/Return overdue: ${staleRecall} business day${staleRecall === 1 ? '' : 's'} past settlement ${item.pending_recall_settlement_date} — settle it or edit the date`
                    : null,
                  stuckPosting != null ? stuckBoPostingTitle(stuckPosting) : null,
                ].filter((t): t is string => t != null);
                const staleMaturedTitle = staleTitles.length ? staleTitles.join('\n') : undefined;
                return (
                  <div
                    data-row-id={item.id}
                    tabIndex={0}
                    title={staleMaturedTitle}
                    className={clsx(
                      'relative flex items-center border-b border-gray-200 cursor-pointer motion-safe:transition-colors motion-safe:duration-150',
                      'focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue-400 focus-visible:outline-offset-[-2px]',
                      isRowSelected
                        ? 'bg-blue-50 border-l-2 border-l-blue-500 hover:bg-blue-100'
                        : 'hover:bg-gray-50',
                    )}
                    style={GRID_ROW_HEIGHT_STYLE}
                    // Shift+click would otherwise drag a text selection across the rows it
                    // spans; onClick focuses the row itself, so nothing is lost here. Same
                    // exemption as the click below — a control inside the row keeps its own
                    // mousedown (caret placement, drag) even though link and button
                    // activation happens on click and would survive either way.
                    onMouseDown={(e) => {
                      if (!e.shiftKey) return;
                      if ((e.target as HTMLElement).closest(ROW_SELECT_EXEMPT)) return;
                      e.preventDefault();
                    }}
                    onClick={(e) => {
                      // Clicks on action buttons / links / inputs must not touch row selection.
                      if ((e.target as HTMLElement).closest(ROW_SELECT_EXEMPT)) return;
                      // LR-694: focus follows the click. The row is `tabIndex={0}`, but only
                      // Chrome focuses a div on mousedown — Safari and Firefox do not, so the
                      // focus ring stayed on whichever row was focused earlier while the
                      // selection moved on: two rows looking current at once. `preventScroll`
                      // because the blotter is virtualised and scrolls horizontally; the row
                      // is already on screen, and a browser-driven scroll would yank it.
                      e.currentTarget.focus({ preventScroll: true });
                      onRowClick(item.id, selectionModeFromEvent(e));
                    }}
                    onKeyDown={(e) => {
                      // LR-694: the row was focusable with no key handler anywhere — Space and
                      // Enter did nothing, and Space scrolled the container instead. Only the
                      // row itself: a Space/Enter on a link or button inside it belongs to that
                      // control (Enter on the counterparty link must follow the link).
                      if (e.target !== e.currentTarget) return;
                      if (e.key !== ' ' && e.key !== 'Enter') return;
                      // Cmd/Ctrl+Enter opens Trade Entry (use-keyboard-shortcuts branch 8,
                      // capture phase) — leave that chord alone or the row silently drops
                      // out of the selection as the modal opens.
                      if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) return;
                      e.preventDefault();
                      // Same modifiers as a click: plain replaces, Cmd/Ctrl toggles (LR-699).
                      onRowClick(item.id, selectionModeFromEvent(e));
                    }}
                  >
                    {hasPinnedLeft && (
                      <div
                        className={clsx(
                          'sticky left-0 flex items-center self-stretch pl-3 pr-3 py-2 overflow-hidden shadow-[4px_0_6px_-2px_rgba(0,0,0,0.1)]',
                          isRowSelected ? 'bg-blue-50' : 'bg-white',
                        )}
                        style={{ minWidth: `${leftWidth}px`, width: `${leftWidth}px`, zIndex: Z_INDEX.TABLE_STICKY }}
                      >
                        {attentionAccent}
                        {renderBodyGroup(leftCols, item, index)}
                      </div>
                    )}
                    {!hasPinnedLeft && attentionAccent}
                    <div className={clsx('flex items-center py-2', hasPinnedLeft ? 'pl-2' : 'pl-4', hasPinnedRight ? 'pr-2' : 'pr-4')}>
                      {renderBodyGroup(centerCols, item, index)}
                    </div>
                    {hasPinnedRight && (
                      <div
                        className={clsx(
                          'sticky right-0 flex items-center self-stretch pl-3 pr-7 py-2 shadow-[-4px_0_6px_-2px_rgba(0,0,0,0.1)]',
                          isRowSelected ? 'bg-blue-50' : 'bg-white',
                        )}
                        style={{ minWidth: `${rightWidth}px`, width: `${rightWidth}px`, zIndex: Z_INDEX.TABLE_STICKY }}
                      >
                        {renderBodyGroup(rightCols, item, index)}
                      </div>
                    )}
                  </div>
                );
              })()}
        </div>
      )}
    />
  );

  // The loading pill is aria-hidden: a screen reader hears this region instead. It sits
  // outside the aria-busy scroll container and is always mounted, since a live region
  // inserted together with its text, or changing inside a busy subtree, may go unannounced.
  return (
    <>
      {grid}
      <div role="status" className="sr-only">
        {loadingLabel}
      </div>
    </>
  );
});
